// src/data/coverage/measureGroupMap.ts
// ============================================================
// MEASURE -> CATEGORY -> MEASURE GROUP  (LOOKUP TABLE, NO LOGIC)
//
// The 38 active measure ids are sparse across 1-56. This file is the single authored map
// from each id onto its six-way category, and from each category onto its measure group.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHAT A MEASURE GROUP IS, AND WHAT IT IS NOT.
//
// These four groups are a READABILITY GROUPING FOR COVERAGE REPORTING. They exist so the
// coverage panel can say "13 of 21 informed" about a block a reader can hold in their head,
// instead of listing 38 rows.
//
// They are NOT presented as the VRI's index structure. The VRI's index structure is still
// being refined, and nothing in this file should be read as stating it. In particular:
//   · no weights live here, and none should — a weight would make this a scoring statement
//   · a group boundary moving is a reporting change, not a scoring change
//   · the composite score itself is the VRI, and it is named that wherever it appears
//
// The grouping deliberately matches what the repo already uses, so a reader is not asked to
// learn a second taxonomy: DIM_META in prototype/data/adapters/useCaseContentTypes.ts and
// the 6-to-4 bridge in prototype/utils/weighting.ts both carry the same four names and the
// same measure assignment.
// ─────────────────────────────────────────────────────────────────────────────
//
// The category half is NOT invented here either. It mirrors STAGE_MEASURE_TO_CATEGORY in
// src/prototype/utils/weighting.ts, which carries the dva-stage m_categories assignment
// verbatim. A drift test asserts the two agree. Duplicated rather than imported because
// src/data must not reach into src/prototype (the same shared-module rule that put
// demoSeedVersion in src/lib).
// ============================================================

export type CoverageCategory =
  | 'Financial Value'
  | 'Strategic Value'
  | 'Data Readiness'
  | 'Compliance & Risk'
  | 'Organizational Readiness'
  | 'Implementation & Market';

/** The four reporting groups. Same names and membership as DIM_META. */
export type MeasureGroup =
  | 'Financial Impact'
  | 'Strategic Alignment'
  | 'Operational Urgency'
  | 'Implementation Feasibility';

/** Every category, in display order. */
export const COVERAGE_CATEGORIES: readonly CoverageCategory[] = [
  'Financial Value',
  'Strategic Value',
  'Data Readiness',
  'Compliance & Risk',
  'Organizational Readiness',
  'Implementation & Market',
] as const;

/** Every group, in display order. */
export const MEASURE_GROUPS: readonly MeasureGroup[] = [
  'Financial Impact',
  'Strategic Alignment',
  'Operational Urgency',
  'Implementation Feasibility',
] as const;

/** measure id -> category. The 38 active ids, sparse across 1-56. */
export const MEASURE_TO_CATEGORY: Readonly<Record<number, CoverageCategory>> = {
  1: 'Financial Value',
  2: 'Financial Value',
  3: 'Financial Value',
  4: 'Financial Value',

  5: 'Strategic Value',
  6: 'Strategic Value',
  7: 'Strategic Value',
  8: 'Strategic Value',
  21: 'Strategic Value',
  22: 'Strategic Value',
  24: 'Strategic Value',

  26: 'Data Readiness',
  27: 'Data Readiness',
  28: 'Data Readiness',
  29: 'Data Readiness',
  30: 'Data Readiness',
  31: 'Data Readiness',
  32: 'Data Readiness',

  34: 'Compliance & Risk',
  35: 'Compliance & Risk',
  36: 'Compliance & Risk',
  37: 'Compliance & Risk',
  38: 'Compliance & Risk',
  39: 'Compliance & Risk',
  40: 'Compliance & Risk',
  41: 'Compliance & Risk',

  42: 'Organizational Readiness',
  43: 'Organizational Readiness',
  44: 'Organizational Readiness',
  45: 'Organizational Readiness',
  46: 'Organizational Readiness',
  47: 'Organizational Readiness',

  51: 'Implementation & Market',
  52: 'Implementation & Market',
  53: 'Implementation & Market',
  54: 'Implementation & Market',
  55: 'Implementation & Market',
  56: 'Implementation & Market',
};

/**
 * category -> group.
 *
 * Three categories share Implementation Feasibility; the fourth group, Operational Urgency,
 * is the Implementation & Market category on its own (measures 51-56). That is the same
 * placement the 6-to-4 bridge uses, where stage category 6 becomes its own dimension rather
 * than folding into feasibility.
 */
export const CATEGORY_TO_GROUP: Readonly<Record<CoverageCategory, MeasureGroup>> = {
  'Financial Value': 'Financial Impact',
  'Strategic Value': 'Strategic Alignment',
  'Data Readiness': 'Implementation Feasibility',
  'Compliance & Risk': 'Implementation Feasibility',
  'Organizational Readiness': 'Implementation Feasibility',
  'Implementation & Market': 'Operational Urgency',
};

/** The 38 active measure ids, ascending. Derived from the map so it cannot fall out of sync. */
export const COVERAGE_MEASURE_IDS: readonly number[] = Object.keys(MEASURE_TO_CATEGORY)
  .map(Number)
  .sort((a, b) => a - b);

/** Total measures in the catalog. Derived, never typed. */
export const MEASURE_COUNT = COVERAGE_MEASURE_IDS.length;

export function categoryForMeasure(measureId: number): CoverageCategory | undefined {
  return MEASURE_TO_CATEGORY[measureId];
}

export function groupForMeasure(measureId: number): MeasureGroup | undefined {
  const category = MEASURE_TO_CATEGORY[measureId];
  return category === undefined ? undefined : CATEGORY_TO_GROUP[category];
}

/** category -> its measure ids (inverse of MEASURE_TO_CATEGORY). */
export const CATEGORY_TO_MEASURE_IDS: Readonly<Record<CoverageCategory, number[]>> = (() => {
  const out = {} as Record<CoverageCategory, number[]>;
  COVERAGE_CATEGORIES.forEach((c) => {
    out[c] = [];
  });
  COVERAGE_MEASURE_IDS.forEach((id) => {
    out[MEASURE_TO_CATEGORY[id]].push(id);
  });
  return out;
})();

/** group -> its measure ids. Produces 4 / 7 / 6 / 21. */
export const GROUP_TO_MEASURE_IDS: Readonly<Record<MeasureGroup, number[]>> = (() => {
  const out = {} as Record<MeasureGroup, number[]>;
  MEASURE_GROUPS.forEach((g) => {
    out[g] = [];
  });
  COVERAGE_MEASURE_IDS.forEach((id) => {
    out[CATEGORY_TO_GROUP[MEASURE_TO_CATEGORY[id]]].push(id);
  });
  return out;
})();
